import React, { ChangeEvent, FormEvent, useEffect, useMemo, useState } from 'react'
import { DataCard, DataColumn, DataGrid } from '@/components/DataCard/DataCard'
import {
  FormCheckboxField,
  FormDateField,
  FormField,
  FormFieldLayout,
  FormMultiSelectField,
  FormSelectField,
} from '@/components/Form/FormField'
import type { SelectMenuItem, SelectValue } from '@/components/Select'
import { Button } from '@/components/ui/button'
import { ButtonGroup } from '@/components/ui/button-group'
import { Dialog } from '@/components/ui/dialog'
import { LabeledValue } from 'antd/es/select'
import { NameDto } from 'api/Common/Types'
import { TickmillCompanyDto } from 'api/Identity/Types/Company'
import { PermissionDto } from 'api/Identity/Types/PermissionDto'
import { CampaignTypeDto } from 'api/Trading-Accounts/Types/CampaignTypeDto'
import { InstrumentDto } from 'api/Trading-Accounts/Types/InstrumentDto'
import {
  getMarketingCampaignName,
  MarketingCampaignType,
} from 'api/Trading-Accounts/Types/MarketingCampaignType'
import { isMT5Platform, PlatformTypeDto } from 'api/Trading-Accounts/Types/PlatformTypeDto'
import { AddUpdateMarketingCampaign } from 'api/Transaction/Types/AddUpdateMarketingCampaign'
import { RestrictionDto } from 'api/Wallet/Types/RestrictionDto'
import { CurrencyType } from 'api/Wallet/Types/Wallet'
import { WalletCurrencyDto } from 'api/Wallet/Types/WalletCurrencyDto'
import classNames from 'classnames'
import { CheckboxSwitch } from 'components/checkboxSwitch/CheckboxSwitch'
import { Form, FormikErrors, FormikProps, withFormik } from 'formik'
import { useApiClient } from 'utils/api/ApiClient'
import { TradingAccountsApiClient } from 'utils/api/tradingAccountsApi'
import { createDateAsUTC } from 'utils/date'
import { FormSubmitValues, UrlPattern } from 'utils/formValidation'
import { isEmpty } from 'utils/is-empty'
import { isNumeric } from 'utils/is-numeric'
import { isString } from 'utils/is-string'

import { CampaignDetailsBusinessRuleModal } from './CampaignDetailsPage/CampaignDetailsBusinessRuleModal'
import {
  BusinessRule,
  CampaignDetailsBusinessRulesTable,
} from './CampaignDetailsPage/CampaignDetailsBusinessRulesTable'
import { CampaignDetailsCurrencyModal } from './CampaignDetailsPage/CampaignDetailsCurrencyModal'
import { CampaignDetailsCurrencyTable } from './CampaignDetailsPage/CampaignDetailsCurrencyTable'
import {
  CampaignRebateConfiguration,
  CampaignRebateConfigurationModal,
} from './CampaignDetailsPage/CampaignRebateConfigurationModal/CampaignRebateConfigurationModal'
import { CampaignRebateConfigurationTable } from './CampaignDetailsPage/CampaignRebateConfigurationTable'

import styles from './AddCampaignDetailsForm.module.scss'

interface CampaignDetailsFormValues {
  id?: string
  name: string
  key: string
  typeId: number | undefined
  calculationTypeId: number | undefined
  to: string | null
  from: string | null
  enrollmentTo: string | null
  disqualificationEmailTemplate: string | null
  termsAndConditionsUrl: string | null
  defaultCurrencyId: CurrencyType
  currencies: LabeledValue[]
  currenciesMinDeposit: Record<CurrencyType, number>
  statusId: number
  intervalId: number
  symbols: string[]
  rebates: CampaignRebateConfiguration[]
  minDeposit: number | undefined
  maxPayout: number | undefined
  intervalValue: number
  calculatePayouts: boolean
  issuePayouts: boolean
  payoutIntervalId: number
  approvePayouts: boolean
  tickmillCompanies: number[]
  platformTypes: number[]
  restrictions: number[]
  businessRules: BusinessRule[]
  luckyDrawConfiguration: {
    expirationIntervalId: number | undefined
    expirationIntervalValue: number | undefined
    maxDailyUsableDrawChances: number | undefined
    maxDailyGeneratedDrawChances: number | undefined
    maxDrawChancesSingleTransaction: number | undefined
    maxWinChancesPerClient: number | undefined
    maxWeeklyMaximumRewardsAllowed: number | undefined
    minValueAllowance: number | undefined
    campaignTimeZoneId: string
    maxPayoutDivisionPerDay: number | undefined
  }
  useCustomCurrencyAmount: boolean

  campaignDescription: string
  internalOnly: boolean

  marginBonusConfiguration: {
    campaignMarginBonusTiers: { minDeposit: number; bonusAwardedPercentage: number }[]
    maximumBonus: number | undefined
    campaignMarginBonusWithdrawRuleId: number | undefined
    removeCreditOnStopOut: boolean | undefined
    validForDays: number | undefined
    enrollmentTo: string | null
  }
}

type CampaignDetailsFormProps = FormikProps<CampaignDetailsFormValues> & OuterProps

interface ModalDataProps<T> {
  data: T | undefined
  visible: boolean
}

const useCurrencyModal = (props: CampaignDetailsFormProps) => {
  const { initialValues, setFieldValue } = props

  const [isEditCurrencyModalOpen, setEditCurrencyModalOpen] = useState<boolean>(false)

  const handleCurrency = (event: ChangeEvent<HTMLInputElement>, labeledValue: LabeledValue) => {
    const value = event.target.value
    const minDeposit = value ? Number(value) : null
    setFieldValue(`currenciesMinDeposit.${labeledValue.value}`, minDeposit)
  }

  const handleCurrencyModalOpen = () => {
    setEditCurrencyModalOpen(true)
  }

  const handleCurrencyModalClose = () => {
    setFieldValue('currenciesMinDeposit', initialValues.currenciesMinDeposit)
    setEditCurrencyModalOpen(false)
  }

  const handleSubmitCurrencies = (event: FormEvent) => {
    event.preventDefault()
    setEditCurrencyModalOpen(false)
  }

  return {
    isEditCurrencyModalOpen,
    setEditCurrencyModalOpen,
    handleCurrency,
    handleCurrencyModalOpen,
    handleCurrencyModalClose,
    handleSubmitCurrencies,
  }
}

const useCampaignBusinessRuleModal = (props: CampaignDetailsFormProps) => {
  const { values, setFieldValue } = props

  const initialModalDataState = {
    data: undefined,
    visible: false,
  }

  const [campaignBusinessRuleModal, setCampaignBusinessRuleModal] =
    useState<ModalDataProps<BusinessRule>>(initialModalDataState)

  const handleCampaignBusinessRuleSubmit = (businessRule?: BusinessRule) => {
    if (businessRule) {
      const isNew = !values.businessRules.find((x) => x?.id === businessRule?.id)
      const newBusinessRules = isNew
        ? values.businessRules.concat(businessRule)
        : values.businessRules.map((x) => (x.id === businessRule.id ? businessRule : x))
      setFieldValue('businessRules', newBusinessRules)
      setCampaignBusinessRuleModal(initialModalDataState)
    }
  }

  const handleCampaignBusinessRuleModalRemove = (businessRule?: BusinessRule) => {
    if (businessRule) {
      const newBusinessRules = values.businessRules.filter((x) => x.id !== businessRule.id)
      setFieldValue('businessRules', newBusinessRules)
      setCampaignBusinessRuleModal(initialModalDataState)
    }
  }

  const handleCampaignBusinessRuleModalOpen = (businessRule?: BusinessRule) => {
    setCampaignBusinessRuleModal({
      data: businessRule,
      visible: true,
    })
  }

  const handleCampaignBusinessRuleModalClose = () => {
    setCampaignBusinessRuleModal(initialModalDataState)
  }

  return {
    data: campaignBusinessRuleModal,
    handleCampaignBusinessRuleSubmit,
    handleCampaignBusinessRuleModalOpen,
    handleCampaignBusinessRuleModalRemove,
    handleCampaignBusinessRuleModalClose,
  }
}

const useCampaignRebateConfigurationModal = (props: CampaignDetailsFormProps) => {
  const { values, setFieldValue } = props

  const initialModalDataState = {
    data: undefined,
    visible: false,
  }

  const [campaignRebateConfigurationModal, setCampaignRebateConfigurationModal] =
    useState<ModalDataProps<CampaignRebateConfiguration>>(initialModalDataState)

  const handleCampaignRebateConfigurationSubmit = (rebate?: CampaignRebateConfiguration) => {
    if (rebate) {
      const isNew = !values.rebates.find((x) => x?.id === rebate?.id)
      const newRebates = isNew
        ? values.rebates.concat(rebate)
        : values.rebates.map((x) => (x.id === rebate.id ? rebate : x))
      setFieldValue('rebates', newRebates)
      setCampaignRebateConfigurationModal(initialModalDataState)
    }
  }

  const handleCampaignRebateConfigurationModalRemove = (rebate?: CampaignRebateConfiguration) => {
    if (rebate) {
      const newRebates = values.rebates.filter((x) => x.id !== rebate.id)
      setFieldValue('rebates', newRebates)
      setCampaignRebateConfigurationModal(initialModalDataState)
    }
  }

  const handleCampaignRebateConfigurationModalOpen = (rebate?: CampaignRebateConfiguration) => {
    setCampaignRebateConfigurationModal({
      data: rebate,
      visible: true,
    })
  }

  const handleCampaignRebateConfigurationModalClose = () => {
    setCampaignRebateConfigurationModal(initialModalDataState)
  }

  return {
    data: campaignRebateConfigurationModal,
    handleCampaignRebateConfigurationSubmit,
    handleCampaignRebateConfigurationModalRemove,
    handleCampaignRebateConfigurationModalOpen,
    handleCampaignRebateConfigurationModalClose,
  }
}

const useTypeIdChange = (props: CampaignDetailsFormProps) => {
  const { setFieldValue, onTypeIdChange, intervalTypes } = props

  const handleTypeIdChange = async (value: SelectValue) => {
    if (isNumeric(value)) {
      onTypeIdChange(value).then(() => {
        setFieldValue('typeId', value)
        setFieldValue('calculationTypeId', undefined)
        if (value === MarketingCampaignType.MarginBonus) {
          const dailyIntervalType = intervalTypes.find(
            (type) => type.name.toLowerCase() === 'daily'
          )
          setFieldValue('intervalId', dailyIntervalType?.id)
          setFieldValue('intervalValue', MARGIN_BONUS_DEFAULT_INTERVAL_VALUE)
        }
      })
    }
  }

  return { handleTypeIdChange }
}

type MarginBonusTierError = { minDeposit?: string; bonusAwardedPercentage?: string }

const MARGIN_BONUS_DEFAULT_INTERVAL_VALUE = 1

const AddCampaignDetailsFormUI: React.FC<CampaignDetailsFormProps> = (props) => {
  const {
    values,
    setFieldValue,
    errors,
    tradingAccountTypes = [],
    platformTypes = [],
    currencies = [],
    instruments = [],
    campaignTypes = [],
    campaignStatuses = [],
    campaignBusinessRuleTypes = [],
    calculationTypes = [],
    intervalTypes = [],
    tickmillCompanies = [],
    restrictions = [],
    marginBonusWithdrawRules = [],
  } = props

  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)
  const [isEquivalentInDefaultCurrency, setEquivalentInDefaultCurrency] = useState<boolean>(
    props.values.useCustomCurrencyAmount
  )

  const { handleTypeIdChange } = useTypeIdChange(props)

  const getTierError = (idx: number) => {
    const tiers = errors.marginBonusConfiguration?.campaignMarginBonusTiers
    if (!tiers || typeof tiers === 'string') return undefined
    return (tiers as MarginBonusTierError[])[idx]
  }

  const currencyModal = useCurrencyModal(props)
  const campaignBusinessRuleModal = useCampaignBusinessRuleModal(props)
  const campaignRebateConfigurationModal = useCampaignRebateConfigurationModal(props)

  const handleSubmitForm = () => {
    props.handleSubmit()
  }

  const calculatedClassName = useMemo(
    () =>
      classNames(styles.gridSizeLarge, {
        [styles.gridSizeMedium]: isLuckyDraw(props) || isCampaignOptions(props),
      }),
    [props]
  )

  const isMarginBonus = isMarginBonusType(values.typeId)
  const supportedPlatformTypes = useMemo(
    () => (isMarginBonus ? platformTypes.filter((pt) => isMT5Platform(pt.id)) : platformTypes),
    [isMarginBonus, platformTypes]
  )

  const useInstrumentSelection = (props: CampaignDetailsFormProps) => {
    const { setFieldValue, values } = props

    const [instrumentTypes, setInstrumentTypes] = useState<NameDto[]>()

    useEffect(() => {
      const getInstrumentTypes = async () => {
        try {
          const instrumentTypes = await tradingAccountsApiClient.getTradingAccountInstrumentTypes()
          setInstrumentTypes(instrumentTypes)
        } catch (error) {
          console.error('Error fetching instrument types:', error)
        }
      }
      getInstrumentTypes()
    }, [])

    const handleAssetClassSelect = async (typeId: number | number[]) => {
      try {
        const typeIds = Array.isArray(typeId) ? typeId : [typeId]

        const instruments = await tradingAccountsApiClient.getInstrumentsByTypes(typeIds)

        const newSymbols = instruments.map((instrument: InstrumentDto<string>) => instrument.symbol)

        const updatedSymbols = Array.from(new Set([...values.symbols, ...newSymbols]))
        setFieldValue('symbols', updatedSymbols)
      } catch (error) {
        console.error('Error fetching instruments by type:', error)
      }
    }

    const handleSelectAllInstruments = async () => {
      try {
        const instruments = await tradingAccountsApiClient.getInstruments()

        const allSymbols = instruments.map((instrument) => instrument.symbol)
        setFieldValue('symbols', allSymbols)
      } catch (error) {
        console.error('Error fetching all instruments:', error)
      }
    }

    return {
      instrumentTypes,
      handleAssetClassSelect,
      handleSelectAllInstruments,
    }
  }

  const { instrumentTypes, handleAssetClassSelect, handleSelectAllInstruments } =
    useInstrumentSelection(props)

  const instrumentOptions = useMemo(
    () =>
      [...instruments]
        .sort((a, b) => a.symbol.localeCompare(b.symbol))
        .map((instrument) => ({ id: instrument.symbol, name: instrument.symbol })),
    [instruments]
  )
  const instrumentMenuItems = useMemo<SelectMenuItem[]>(
    () => [
      { type: 'label', key: 'quick-select-label', label: 'Quick Select' },
      {
        type: 'action',
        key: 'quick-select-all',
        label: 'All',
        onSelect: () => {
          void handleSelectAllInstruments()
        },
      },
      ...(instrumentTypes ?? []).map((type) => ({
        type: 'action' as const,
        key: `quick-select-${type.id}`,
        label: type.name,
        onSelect: () => {
          void handleAssetClassSelect(type.id)
        },
      })),
    ],
    [handleAssetClassSelect, handleSelectAllInstruments, instrumentTypes]
  )

  return (
    <React.Fragment>
      {currencyModal.isEditCurrencyModalOpen && (
        <Dialog
          open={currencyModal.isEditCurrencyModalOpen}
          onOpenChange={(open) => {
            if (!open) {
              currencyModal.handleCurrencyModalClose()
            }
          }}
        >
          <CampaignDetailsCurrencyModal
            currencies={values.currencies}
            currenciesMinDeposit={values.currenciesMinDeposit}
            onChange={currencyModal.handleCurrency}
            onSubmit={currencyModal.handleSubmitCurrencies}
          />
        </Dialog>
      )}
      {campaignBusinessRuleModal.data && (
        <Dialog
          open={campaignBusinessRuleModal.data?.visible ?? false}
          onOpenChange={(open) => {
            if (!open) {
              campaignBusinessRuleModal.handleCampaignBusinessRuleModalClose()
            }
          }}
        >
          <CampaignDetailsBusinessRuleModal
            data={campaignBusinessRuleModal.data?.data}
            businessRules={values.businessRules}
            campaignBusinessRuleTypes={campaignBusinessRuleTypes}
            onSubmit={campaignBusinessRuleModal.handleCampaignBusinessRuleSubmit}
          />
        </Dialog>
      )}
      {campaignRebateConfigurationModal.data && (
        <Dialog
          open={campaignRebateConfigurationModal.data?.visible ?? false}
          onOpenChange={(open) => {
            if (!open) {
              campaignRebateConfigurationModal.handleCampaignRebateConfigurationModalClose()
            }
          }}
        >
          <CampaignRebateConfigurationModal
            data={campaignRebateConfigurationModal.data?.data}
            rebates={values.rebates}
            calculationTypeId={values.calculationTypeId}
            tradingAccountTypes={tradingAccountTypes}
            calculationTypes={calculationTypes}
            onSubmit={campaignRebateConfigurationModal.handleCampaignRebateConfigurationSubmit}
          />
        </Dialog>
      )}
      <div className='px-4 md:px-6'>
        <Form>
          <DataGrid rightAddon={<ActionButtons {...props} onSubmit={handleSubmitForm} />}>
            <DataColumn>
              <DataCard title='Marketing Campaigns Options'>
                <FormField
                  name='name'
                  label='Campaign Name (Client Facing) *'
                  error={errors.name}
                />
                <FormField name='key' label='Campaign Key (internal) *' error={errors.key} />
                <FormField
                  name='campaignDescription'
                  label='Campaign Description'
                  error={errors.campaignDescription}
                />
                <FormSelectField
                  name='typeId'
                  label='Campaign Type *'
                  error={errors.typeId}
                  value={values.typeId}
                  options={campaignTypes}
                  onChange={(value) => {
                    void handleTypeIdChange(value as SelectValue)
                  }}
                />
                <FormSelectField
                  name='calculationTypeId'
                  label='Calculation Type *'
                  error={errors.calculationTypeId}
                  value={values.calculationTypeId}
                  options={calculationTypes}
                  onChange={(value) => setFieldValue('calculationTypeId', value)}
                />
                <FormDateField
                  name='from'
                  label='Start Date *'
                  showTimeSelect
                  error={errors.from}
                />
                <FormDateField name='to' label='End Date *' showTimeSelect error={errors.to} />
                <FormField
                  name='termsAndConditionsUrl'
                  error={errors.termsAndConditionsUrl}
                  label='Terms & Conditions URL *'
                />
                <FormSelectField
                  name='defaultCurrencyId'
                  label='Default Currency *'
                  value={values.defaultCurrencyId}
                  error={errors.defaultCurrencyId}
                  options={currencies}
                  onChange={(value) => setFieldValue('defaultCurrencyId', value)}
                />
                <SelectCurrencyField
                  currencies={values.currencies}
                  currenciesMinDeposit={values.currenciesMinDeposit}
                  error={typeof errors.currencies === 'string' ? errors.currencies : undefined}
                  disabled={false}
                  options={currencies}
                  onChange={(labeledValues, currenciesMinDeposit) => {
                    setFieldValue('currencies', labeledValues)
                    setFieldValue('currenciesMinDeposit', currenciesMinDeposit)
                  }}
                />
                <FormSelectField
                  name='statusId'
                  label='Status *'
                  error={errors.statusId}
                  value={values.statusId}
                  options={campaignStatuses}
                  onChange={(value) => setFieldValue('statusId', Number(value))}
                />
                <FormSelectField
                  name='intervalId'
                  label='Interval Type *'
                  value={values.intervalId}
                  options={intervalTypes}
                  disabled={isMarginBonus}
                  error={errors.intervalId}
                  onChange={(value) => setFieldValue('intervalId', Number(value))}
                />
                <FormField
                  name='intervalValue'
                  label='Interval Value *'
                  type='number'
                  error={errors.intervalValue}
                  disabled={isMarginBonus}
                />
                <FormMultiSelectField
                  name='symbols'
                  label='Applicable Symbols *'
                  value={values.symbols}
                  options={instrumentOptions}
                  menuItems={instrumentMenuItems}
                  allowClear
                  error={errors.symbols?.toString()}
                  showSearch
                  onChange={(value) =>
                    setFieldValue(
                      'symbols',
                      Array.isArray(value) ? value.map(String) : value ? [String(value)] : []
                    )
                  }
                />
                <FormMultiSelectField
                  name='platformTypes'
                  label='Available Platform Types *'
                  value={values.platformTypes}
                  options={supportedPlatformTypes}
                  error={errors.platformTypes?.toString()}
                  onChange={(value) =>
                    setFieldValue(
                      'platformTypes',
                      Array.isArray(value)
                        ? value.map((item) => Number(item))
                        : value
                          ? [Number(value)]
                          : []
                    )
                  }
                />
                <FormMultiSelectField
                  name='tickmillCompanies'
                  label='Tickmill Companies *'
                  value={values.tickmillCompanies}
                  placeholder='Companies'
                  error={errors.tickmillCompanies?.toString()}
                  options={tickmillCompanies}
                  onChange={(value) =>
                    setFieldValue(
                      'tickmillCompanies',
                      Array.isArray(value)
                        ? value.map((item) => Number(item))
                        : value
                          ? [Number(value)]
                          : []
                    )
                  }
                />
                <FormCheckboxField name='internalOnly' label='Internal Only' />
              </DataCard>
              <DataCard title='Campaign Restrictions'>
                <FormMultiSelectField
                  name='restrictions'
                  label='Restrictions'
                  error={errors.restrictions?.toString()}
                  value={values.restrictions}
                  options={restrictions}
                  onChange={(value) =>
                    setFieldValue(
                      'restrictions',
                      Array.isArray(value)
                        ? value.map((item) => Number(item))
                        : value
                          ? [Number(value)]
                          : []
                    )
                  }
                />
              </DataCard>
              <DataCard title='Campaign Business Rules'>
                <CampaignDetailsBusinessRulesTable
                  isEdit
                  businessRules={values.businessRules}
                  onAdd={campaignBusinessRuleModal.handleCampaignBusinessRuleModalOpen}
                  onEdit={campaignBusinessRuleModal.handleCampaignBusinessRuleModalOpen}
                  onRemove={campaignBusinessRuleModal.handleCampaignBusinessRuleModalRemove}
                  className={calculatedClassName}
                />
              </DataCard>
            </DataColumn>
            <DataColumn>
              {isCampaignOptions(props) && (
                <DataCard title='Campaign Options'>
                  <FormField
                    name='minDeposit'
                    label='Minimum Deposit'
                    type='number'
                    error={errors.minDeposit}
                    disabled={isEquivalentInDefaultCurrency}
                  />
                  <FormDateField
                    name='enrollmentTo'
                    label='Enrollment To Date'
                    showTimeSelect
                    error={errors.enrollmentTo}
                  />
                  <FormField
                    name='disqualificationEmailTemplate'
                    label='Disqualification Template'
                  />
                  <FormCheckboxField
                    name='useCustomCurrencyAmount'
                    label='Use Custom Currency Amount'
                    value={values.useCustomCurrencyAmount}
                    onChange={(checked) => {
                      setFieldValue('useCustomCurrencyAmount', checked)
                      setEquivalentInDefaultCurrency(checked)
                    }}
                  />
                  {isEquivalentInDefaultCurrency && (
                    <CampaignDetailsCurrencyTable
                      currencies={values.currencies}
                      currenciesMinDeposit={values.currenciesMinDeposit}
                      isEdit
                      onCurrencyModalOpen={currencyModal.handleCurrencyModalOpen}
                      className={styles.gridSizeMedium}
                    />
                  )}
                </DataCard>
              )}

              {isCampaignRebateOption(props) && (
                <DataCard title='Rebate Options'>
                  <FormField
                    name='maxPayout'
                    label='Maximum Payout'
                    type='number'
                    error={errors.maxPayout}
                  />
                  <SwitchField
                    label='Issue Payouts'
                    value={values.issuePayouts}
                    onChange={(value) => setFieldValue('issuePayouts', value)}
                    error={errors.issuePayouts}
                  />
                  {values.issuePayouts && (
                    <>
                      <SwitchField
                        label='Autoapprove Payouts'
                        value={values.approvePayouts}
                        onChange={(value) => setFieldValue('approvePayouts', value)}
                        error={errors.approvePayouts}
                      />
                      <FormSelectField
                        name='payoutIntervalId'
                        label='Payout Period'
                        value={values.payoutIntervalId}
                        options={intervalTypes}
                        error={errors.payoutIntervalId}
                        onChange={(value) => setFieldValue('payoutIntervalId', Number(value))}
                      />
                    </>
                  )}

                  {!!values.calculationTypeId && (
                    <CampaignRebateConfigurationTable
                      isEdit
                      calculationTypeId={values.calculationTypeId}
                      tradingAccountTypes={tradingAccountTypes}
                      rebates={values.rebates}
                      onAdd={
                        campaignRebateConfigurationModal.handleCampaignRebateConfigurationModalOpen
                      }
                      onEdit={
                        campaignRebateConfigurationModal.handleCampaignRebateConfigurationModalOpen
                      }
                      onRemove={
                        campaignRebateConfigurationModal.handleCampaignRebateConfigurationModalRemove
                      }
                      className={styles.gridSizeMedium}
                    />
                  )}
                </DataCard>
              )}
              {isLuckyDraw(props) && (
                <DataCard title={`${getMarketingCampaignName(values.typeId)} Options`}>
                  <FormField
                    name='minDeposit'
                    label='Minimum Deposit'
                    type='number'
                    error={errors.minDeposit}
                  />
                  <FormField
                    name='maxPayout'
                    label='Maximum Payout'
                    type='number'
                    error={errors.maxPayout}
                  />
                  <FormField
                    name='luckyDrawConfiguration.maxDailyUsableDrawChances'
                    label='Max Daily Usable Draw Chances'
                    type='number'
                    error={errors.luckyDrawConfiguration?.maxDailyUsableDrawChances}
                  />
                  <FormField
                    name='luckyDrawConfiguration.maxDailyGeneratedDrawChances'
                    label='Max Daily Generated Draw Chances'
                    type='number'
                    error={errors.luckyDrawConfiguration?.maxDailyGeneratedDrawChances}
                  />
                  <FormField
                    name='luckyDrawConfiguration.maxDrawChancesSingleTransaction'
                    label='Max Draw Chances Single Transaction'
                    type='number'
                    error={errors.luckyDrawConfiguration?.maxDrawChancesSingleTransaction}
                  />
                  <FormField
                    error={errors.luckyDrawConfiguration?.maxWinChancesPerClient}
                    name='luckyDrawConfiguration.maxWinChancesPerClient'
                    label='Max Win Chances Per Client'
                    type='number'
                  />
                  <FormField
                    name='luckyDrawConfiguration.maxWeeklyMaximumRewardsAllowed'
                    error={errors.luckyDrawConfiguration?.maxWeeklyMaximumRewardsAllowed}
                    label='Max Weekly Maximum Rewards All'
                    type='number'
                  />
                  <FormField
                    error={errors.luckyDrawConfiguration?.minValueAllowance}
                    name='luckyDrawConfiguration.minValueAllowance'
                    label='Min Value Allowance'
                    type='number'
                  />
                  <FormSelectField
                    name='luckyDrawConfiguration.expirationIntervalId'
                    label='Expiration Interval Type'
                    error={errors.luckyDrawConfiguration?.expirationIntervalId}
                    value={values.luckyDrawConfiguration.expirationIntervalId}
                    options={intervalTypes}
                    onChange={(value) =>
                      setFieldValue(
                        'luckyDrawConfiguration.expirationIntervalId',
                        value ? Number(value) : undefined
                      )
                    }
                  />
                  <FormField
                    error={errors.luckyDrawConfiguration?.expirationIntervalValue}
                    name='luckyDrawConfiguration.expirationIntervalValue'
                    label='Expiration Interval Value'
                    type='number'
                  />
                  {isEquivalentInDefaultCurrency && (
                    <CampaignDetailsCurrencyTable
                      isEdit
                      currencies={values.currencies}
                      currenciesMinDeposit={values.currenciesMinDeposit}
                      onCurrencyModalOpen={currencyModal.handleCurrencyModalOpen}
                    />
                  )}
                  <FormField
                    name='luckyDrawConfiguration.maxPayoutDivisionPerDay'
                    label='Max Payout Division Per Day'
                    type='number'
                  />
                </DataCard>
              )}
              {isMarginBonus && (
                <DataCard title={`${getMarketingCampaignName(values.typeId)} Options`}>
                  <div className='space-y-4'>
                    <div className='grid grid-cols-[1fr_2fr_2fr] gap-4 text-sm font-medium'>
                      <div>Bonus Tiers</div>
                      <div>Condition (min Deposit)</div>
                      <div>Bonus Awarded</div>
                    </div>
                    {values.marginBonusConfiguration.campaignMarginBonusTiers.map((_, idx) => {
                      const tierErrors = getTierError(idx)
                      return (
                        <div
                          key={`mb-tier-${idx}`}
                          className='grid grid-cols-[1fr_2fr_2fr] gap-4 items-start'
                        >
                          <div />
                          <FormField
                            name={`marginBonusConfiguration.campaignMarginBonusTiers.${idx}.minDeposit`}
                            label='Min Deposit *'
                            type='number'
                            error={tierErrors?.minDeposit}
                          />
                          <FormFieldLayout
                            error={tierErrors?.bonusAwardedPercentage}
                            label='Bonus Awarded *'
                            name={`marginBonusConfiguration.campaignMarginBonusTiers.${idx}.bonusAwardedPercentage`}
                            rightAddon={
                              <Button
                                key={`remove-tier-${idx}`}
                                type='button'
                                variant='outline'
                                onClick={() => {
                                  const next = [
                                    ...values.marginBonusConfiguration.campaignMarginBonusTiers,
                                  ]
                                  next.splice(idx, 1)
                                  setFieldValue(
                                    'marginBonusConfiguration.campaignMarginBonusTiers',
                                    next
                                  )
                                }}
                              >
                                X
                              </Button>
                            }
                          >
                            <input
                              className='flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-xs outline-none'
                              type='number'
                              min='0'
                              value={
                                values.marginBonusConfiguration.campaignMarginBonusTiers[idx]
                                  ?.bonusAwardedPercentage ?? ''
                              }
                              onChange={(event) =>
                                setFieldValue(
                                  `marginBonusConfiguration.campaignMarginBonusTiers.${idx}.bonusAwardedPercentage`,
                                  event.target.value
                                )
                              }
                            />
                          </FormFieldLayout>
                        </div>
                      )
                    })}
                    <Button
                      key='add-tier'
                      type='button'
                      variant='outline'
                      onClick={() => {
                        setFieldValue('marginBonusConfiguration.campaignMarginBonusTiers', [
                          ...values.marginBonusConfiguration.campaignMarginBonusTiers,
                          { minDeposit: 0, bonusAwardedPercentage: 0 },
                        ])
                      }}
                    >
                      +
                    </Button>
                    <FormField
                      name='marginBonusConfiguration.maximumBonus'
                      label='Maximum Bonus *'
                      type='number'
                      error={errors.marginBonusConfiguration?.maximumBonus}
                    />
                    <FormSelectField
                      name='marginBonusConfiguration.campaignMarginBonusWithdrawRuleId'
                      label='Withdrawal Rule *'
                      value={values.marginBonusConfiguration.campaignMarginBonusWithdrawRuleId}
                      options={marginBonusWithdrawRules}
                      onChange={(value) =>
                        setFieldValue(
                          'marginBonusConfiguration.campaignMarginBonusWithdrawRuleId',
                          value ? Number(value) : undefined
                        )
                      }
                    />
                    <FormCheckboxField
                      name='marginBonusConfiguration.removeCreditOnStopOut'
                      label='Remove Credit on Stop Out'
                      value={values.marginBonusConfiguration.removeCreditOnStopOut}
                    />
                    <FormField
                      name='marginBonusConfiguration.validForDays'
                      label='Bonus Valid for Days'
                      type='number'
                    />
                    <FormDateField
                      error={errors.marginBonusConfiguration?.enrollmentTo}
                      name='marginBonusConfiguration.enrollmentTo'
                      label='Enrollment To Date *'
                      showTimeSelect
                    />
                  </div>
                </DataCard>
              )}
            </DataColumn>
          </DataGrid>
        </Form>
      </div>
    </React.Fragment>
  )
}

interface ActionButtonsProps extends Omit<CampaignDetailsFormProps, 'onSubmit'> {
  onSubmit(): void
}

const ActionButtons: React.FC<ActionButtonsProps> = (props) => {
  const { isValid, onSubmit } = props

  return (
    <ButtonGroup>
      <Button key='button' type='button' onClick={onSubmit} disabled={!isValid}>
        Save
      </Button>
    </ButtonGroup>
  )
}

const SwitchField: React.FC<{
  label: React.ReactNode
  value: boolean
  onChange(value: boolean): void
  error?: React.ReactNode
  disabled?: boolean
}> = ({ label, value, onChange, error, disabled }) => (
  <FormFieldLayout label={label} error={error?.toString()}>
    <CheckboxSwitch
      name={typeof label === 'string' ? label : 'toggle'}
      initialValue={value}
      disabled={disabled}
      onChange={onChange}
    />
  </FormFieldLayout>
)

const SelectCurrencyField: React.FC<{
  currencies: LabeledValue[]
  currenciesMinDeposit: Record<CurrencyType, number>
  error?: string
  disabled?: boolean
  options: { id: CurrencyType; name: string }[]
  onChange(labeledValues: LabeledValue[], currenciesMinDeposit: Record<CurrencyType, number>): void
}> = ({ currencies, currenciesMinDeposit, error, disabled, options, onChange }) => {
  const handleChange = (value: string[] | string | number | number[] | undefined) => {
    const selectedValues = Array.isArray(value) ? value : value != null ? [value] : []
    const labeledValues: LabeledValue[] = selectedValues
      .map((currencyId) => options.find((option) => option.id === currencyId))
      .filter((option): option is { id: CurrencyType; name: string } => Boolean(option))
      .map((option) => ({
        label: option.name,
        value: option.id,
      }))

    const currentCurrencyIds = new Set(currencies.map((currency) => currency.value as CurrencyType))
    const nextCurrenciesMinDeposit = labeledValues.reduce(
      (acc, currency) => {
        const currencyId = currency.value as CurrencyType
        acc[currencyId] = currentCurrencyIds.has(currencyId)
          ? currenciesMinDeposit[currencyId] || 0
          : 0
        return acc
      },
      {} as Record<CurrencyType, number>
    )

    onChange(labeledValues, nextCurrenciesMinDeposit)
  }

  return (
    <FormMultiSelectField
      error={error}
      options={options}
      label='Applicable Currencies *'
      name='currencies'
      disabled={disabled}
      value={currencies.map((currency) => currency.value)}
      onChange={handleChange}
    />
  )
}

interface OuterProps {
  campaignTypeId: number | undefined
  permissions: PermissionDto[]
  restrictions: RestrictionDto[]
  dateFormatType?: string

  tradingAccountTypes: NameDto[]
  instruments: InstrumentDto[]
  platformTypes: PlatformTypeDto[]
  currencies: WalletCurrencyDto[]
  intervalTypes: NameDto[]
  campaignStatuses: NameDto[]
  campaignBusinessRuleTypes: NameDto[]
  campaignTypes: CampaignTypeDto[]
  calculationTypes: NameDto[]
  tickmillCompanies: TickmillCompanyDto[]
  marginBonusWithdrawRules: NameDto[]

  onTypeIdChange(typeId: number): Promise<void>
  onSubmit(values: FormSubmitValues<AddUpdateMarketingCampaign>): Promise<void>
}

const dataToSubmit = (
  props: OuterProps,
  values: CampaignDetailsFormValues
): FormSubmitValues<AddUpdateMarketingCampaign> => {
  const { instruments, tickmillCompanies } = props

  const payload: FormSubmitValues<AddUpdateMarketingCampaign> = {
    key: values.key,
    name: values.name,
    typeId: Number.parseInt(values.typeId!.toString(), 10),
    calculationTypeId: values.calculationTypeId
      ? Number.parseInt(values.calculationTypeId.toString(), 10)
      : null,
    from: values.from ? createDateAsUTC(values.from).toISOString() : null,
    to: values.to ? createDateAsUTC(values.to).toISOString() : null,
    enrollmentTo:
      values.enrollmentTo || values.marginBonusConfiguration.enrollmentTo
        ? createDateAsUTC(
            values.enrollmentTo || values.marginBonusConfiguration.enrollmentTo!,
            true
          ).toISOString()
        : null,
    disqualificationEmailTemplate: values.disqualificationEmailTemplate || null,
    statusId: values.statusId,
    termsAndConditionsUrl: values.termsAndConditionsUrl || null,
    minDeposit: values.minDeposit || null,
    maxPayout: values.maxPayout || null,
    defaultCurrencyId: values.defaultCurrencyId,
    intervalId: values.intervalId,
    intervalValue: values.intervalValue,
    calculatePayouts: values.calculatePayouts,
    issuePayouts: values.issuePayouts,
    payoutIntervalId: values.payoutIntervalId,
    currencies: Object.keys(values.currenciesMinDeposit).map((currencyId) => ({
      currencyId,
      minDeposit: values.currenciesMinDeposit[currencyId as CurrencyType],
    })),
    countryOverrides: [],
    symbols: (values.symbols || [])
      .map((symbol) => {
        const instrument = instruments.find((x) => x.symbol === symbol)
        return instrument?.symbol
      })
      .filter((x): x is string => !!x),
    rebates:
      values.rebates.map((x) => ({
        ...x,
        campaignCalculationTypeId: x.campaignCalculationTypeId.id,
      })) || [],
    tickmillCompanies: (values.tickmillCompanies || [])
      .map((companyId) => {
        const tickmillCompany = tickmillCompanies.find((x) => x.id === companyId)
        return tickmillCompany?.id
      })
      .filter((x): x is number => !!x)
      .map((x) => Number.parseInt(x.toString(), 10))
      .filter((x): x is number => !!x),
    platformTypes: values.platformTypes
      .filter((x): x is number => !!x)
      .map((x) => Number.parseInt(x.toString(), 10)),
    approvePayouts: values.approvePayouts || false,
    restrictions: values.restrictions,
    businessRules: values.businessRules.map((x) => ({
      businessRuleId: x.rule.id,
      businessRuleValue: x.value,
    })),
    luckyDrawConfiguration: {
      minValueAllowance: values.luckyDrawConfiguration.minValueAllowance || 0,
      expirationIntervalId: values.luckyDrawConfiguration.expirationIntervalId || 0,
      expirationIntervalValue: values.luckyDrawConfiguration.expirationIntervalValue || 0,
      maxDailyUsableDrawChances: values.luckyDrawConfiguration.maxDailyUsableDrawChances || null,
      maxDailyGeneratedDrawChances:
        values.luckyDrawConfiguration.maxDailyGeneratedDrawChances || null,
      maxDrawChancesSingleTransaction:
        values.luckyDrawConfiguration.maxDrawChancesSingleTransaction || null,
      maxWinChancesPerClient: values.luckyDrawConfiguration.maxWinChancesPerClient || null,
      maxWeeklyMaximumRewardsAllowed:
        values.luckyDrawConfiguration.maxWeeklyMaximumRewardsAllowed || null,
      maxPayoutDivisionPerDay: values.luckyDrawConfiguration.maxPayoutDivisionPerDay || null,
    },
    marginBonusConfiguration: isMarginBonusType(values.typeId)
      ? {
          campaignMarginBonusTiers: values.marginBonusConfiguration.campaignMarginBonusTiers || [],
          maximumBonus: values.marginBonusConfiguration.maximumBonus ?? null,
          campaignMarginBonusWithdrawRuleId:
            values.marginBonusConfiguration.campaignMarginBonusWithdrawRuleId ?? null,
          removeCreditOnStopOut: !!values.marginBonusConfiguration.removeCreditOnStopOut,
          validForDays: values.marginBonusConfiguration.validForDays ?? null,
        }
      : null,
    useCustomCurrencyAmount: values.useCustomCurrencyAmount,
    campaignDescription: values.campaignDescription,
    internalOnly: values.internalOnly,
  }
  return payload
}

export const AddCampaignDetailsForm = withFormik<OuterProps, CampaignDetailsFormValues>({
  mapPropsToValues: (props) => {
    const { campaignTypeId } = props
    return {
      id: '',
      key: '',
      name: '',
      typeId: campaignTypeId,
      calculationTypeId: undefined,
      from: '',
      to: '',
      enrollmentTo: null,
      disqualificationEmailTemplate: null,
      statusId: 0,
      termsAndConditionsUrl: '',
      currenciesMinDeposit: {
        USD: 0,
      } as Record<CurrencyType, number>,
      minDeposit: undefined,
      maxPayout: undefined,
      defaultCurrencyId: 'USD',
      intervalId: 0,
      intervalValue: 0,
      calculatePayouts: false,
      issuePayouts: false,
      approvePayouts: false,
      payoutIntervalId: 0,
      currencies: [],
      symbols: [],
      countryOverrides: [],
      tickmillCompanies: [],
      platformTypes: [],
      restrictions: [],
      businessRules: [],
      rebates: [],
      luckyDrawConfiguration: {
        expirationIntervalId: undefined,
        expirationIntervalValue: undefined,
        minValueAllowance: undefined,
        maxDailyUsableDrawChances: undefined,
        maxDailyGeneratedDrawChances: undefined,
        maxDrawChancesSingleTransaction: undefined,
        maxWinChancesPerClient: undefined,
        maxWeeklyMaximumRewardsAllowed: undefined,
        campaignTimeZoneId: '',
        maxPayoutDivisionPerDay: undefined,
      },
      useCustomCurrencyAmount: false,
      campaignDescription: '',
      internalOnly: false,
      marginBonusConfiguration: {
        campaignMarginBonusTiers: [{ minDeposit: 0, bonusAwardedPercentage: 0 }],
        maximumBonus: undefined,
        enrollmentTo: null,
        campaignMarginBonusWithdrawRuleId: undefined,
        removeCreditOnStopOut: false,
        validForDays: undefined,
      },
    }
  },
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      await props.onSubmit(dataToSubmit(props, values))
    } finally {
      setSubmitting(false)
    }
  },
  validate: (values, props) => {
    const { campaignTypes = [], campaignStatuses = [], intervalTypes = [] } = props
    const errors: FormikErrors<CampaignDetailsFormValues> = {}

    if (!values.name) {
      errors.name = 'Required'
    }
    if (!values.key) {
      errors.key = 'Required'
    }
    if (isEmpty(values.currencies)) {
      errors.currencies = 'Required'
    }
    if (
      values.typeId &&
      !campaignTypes.map((x) => x.id).includes(Number.parseInt(values.typeId.toString(), 10))
    ) {
      errors.typeId = 'Required'
    }
    if (!values.calculationTypeId) {
      errors.calculationTypeId = 'Required'
    }

    if (!values.from) {
      errors.from = 'Required'
    } else if (!values.to) {
      errors.to = 'Required'
    } else {
      const fromDate = new Date(values.from)
      const toDate = new Date(values.to)
      if (fromDate > toDate) {
        errors.from = 'Start date must be earlier than end date'
      }
    }

    if (!values.defaultCurrencyId) {
      errors.defaultCurrencyId = 'Required'
    }
    if (
      !campaignStatuses.map((x) => x.id).includes(Number.parseInt(values.statusId.toString(), 10))
    ) {
      errors.statusId = 'Required'
    }
    if (
      !intervalTypes.map((x) => x.id).includes(Number.parseInt(values.intervalId.toString(), 10))
    ) {
      errors.intervalId = 'Required'
    }
    if (values.intervalValue < 0) {
      errors.intervalValue = 'Required'
    }
    if (values.symbols.length <= 0) {
      errors.symbols = 'Required'
    }

    if (values.tickmillCompanies.length <= 0) {
      errors.tickmillCompanies = 'Required'
    }

    if (!values.termsAndConditionsUrl) {
      errors.termsAndConditionsUrl = 'Invalid URL. Please start with https://.'
    }
    if (!!values.termsAndConditionsUrl && !UrlPattern.test(values.termsAndConditionsUrl)) {
      errors.termsAndConditionsUrl = 'Invalid URL. Please start with https://.'
    }

    // Payout Options
    if (isCampaignOptions({ values })) {
      if (values.enrollmentTo && values.from && values.to) {
        const from = new Date(values.from)
        const to = new Date(values.to)
        const enrollmentTo = new Date(values.enrollmentTo)

        if (!(enrollmentTo >= from) || !(enrollmentTo <= to)) {
          errors.enrollmentTo = 'Enrollment date is outside the valid range'
        }
      }
      if (!values.useCustomCurrencyAmount && (!values.minDeposit || values.minDeposit <= 0)) {
        errors.minDeposit = `Invalid min deposit: ${values.minDeposit}`
      }

      if (isCampaignRebateOption({ values })) {
        if (!isMaxPayoutValid(values)) {
          errors.maxPayout = `Invalid max payout: ${values.maxPayout}`
        }
      }
    }

    // LuckyDraw Options
    if (isLuckyDraw({ values })) {
      if (!isLuckyDrawValid(values)) {
        errors.luckyDrawConfiguration = {}
        if (!isLuckyDrawExpirationIntervalIdValid(values)) {
          errors.luckyDrawConfiguration.expirationIntervalId = `Invalid expiration Interval Id: ${
            values.luckyDrawConfiguration.expirationIntervalId || ''
          }`
        }
        if (!isLuckyDrawExpirationIntervalValueValid(values)) {
          errors.luckyDrawConfiguration.expirationIntervalValue = `Invalid expiration Interval Value: ${
            values.luckyDrawConfiguration.expirationIntervalValue || ''
          }`
        }
        if (!isLuckyDrawMinValueAllowanceValid(values)) {
          errors.luckyDrawConfiguration.minValueAllowance = `Invalid min Value Allowance: ${
            values.luckyDrawConfiguration.minValueAllowance || ''
          }`
        }
        if (!isLuckyDrawMaxDailyUsableDrawChancesValid(values)) {
          errors.luckyDrawConfiguration.maxDailyUsableDrawChances = `Invalid max Daily Usable Draw Chances: ${
            values.luckyDrawConfiguration.maxDailyUsableDrawChances || ''
          }`
        }
        if (!isLuckyDrawMaxDailyGeneratedDrawChancesValid(values)) {
          errors.luckyDrawConfiguration.maxDailyGeneratedDrawChances = `Invalid max Daily Generated Draw Chances: ${
            values.luckyDrawConfiguration.maxDailyGeneratedDrawChances || ''
          }`
        }
        if (!isLuckyDrawMaxDrawChancesSingleTransactionValid(values)) {
          errors.luckyDrawConfiguration.maxDrawChancesSingleTransaction = `Invalid max Draw Chances Single Transaction: ${
            values.luckyDrawConfiguration.maxDrawChancesSingleTransaction || ''
          }`
        }
        if (!isLuckyDrawMaxWinChancesPerClientValid(values)) {
          errors.luckyDrawConfiguration.maxWinChancesPerClient = `Invalid max Win Chances Per Client: ${
            values.luckyDrawConfiguration.maxWinChancesPerClient || ''
          }`
        }
        if (!isLuckyDrawMaxWeeklyMaximumRewardsAllowedValid(values)) {
          errors.luckyDrawConfiguration.maxWeeklyMaximumRewardsAllowed = `Invalid max Weekly Maximum Rewards Allowed: ${
            values.luckyDrawConfiguration.maxWeeklyMaximumRewardsAllowed || ''
          }`
        }
      }

      if (!isMaxPayoutValid(values)) {
        errors.maxPayout = `Invalid max payout: ${values.maxPayout || ''}`
      }
    }

    if (!values.platformTypes.length) {
      errors.platformTypes = 'Required'
    }

    if (isMarginBonusType(values.typeId)) {
      const mbErrors: FormikErrors<CampaignDetailsFormValues['marginBonusConfiguration']> = {}

      const config = values.marginBonusConfiguration
      const withdrawRuleId = config.campaignMarginBonusWithdrawRuleId

      if (withdrawRuleId == null) {
        mbErrors.campaignMarginBonusWithdrawRuleId = 'Required'
      }

      const enrollmentTo = values.enrollmentTo ?? values.marginBonusConfiguration.enrollmentTo

      if (enrollmentTo == null) {
        mbErrors.enrollmentTo = 'Required'
      } else {
        const fromDate = values.from ? new Date(values.from) : null
        const toDate = values.to ? new Date(values.to) : null
        const enrollmentToDate = new Date(enrollmentTo)

        if (
          !fromDate ||
          !toDate ||
          Number.isNaN(enrollmentToDate.getTime()) ||
          enrollmentToDate < fromDate ||
          enrollmentToDate > toDate
        ) {
          mbErrors.enrollmentTo = 'Enrollment date must be between start date and end date'
        }
      }

      const maximumBonus = config.maximumBonus

      if (maximumBonus == null || Number.isNaN(maximumBonus)) {
        mbErrors.maximumBonus = 'Required'
      } else if (maximumBonus <= 0) {
        mbErrors.maximumBonus = 'Maximum bonus must be greater than 0'
      }

      const tiers = config.campaignMarginBonusTiers

      if (!tiers.length) {
        mbErrors.campaignMarginBonusTiers = 'Required'
      } else {
        const tierErrors: FormikErrors<
          CampaignDetailsFormValues['marginBonusConfiguration']['campaignMarginBonusTiers'][number]
        >[] = tiers.map((tier) => {
          const tierError: FormikErrors<
            CampaignDetailsFormValues['marginBonusConfiguration']['campaignMarginBonusTiers'][number]
          > = {}

          if (Number.isNaN(tier.minDeposit)) {
            tierError.minDeposit = 'Required'
          } else if (tier.minDeposit <= 0) {
            tierError.minDeposit = 'Minimum deposit must be greater than 0'
          }

          if (Number.isNaN(tier.bonusAwardedPercentage)) {
            tierError.bonusAwardedPercentage = 'Required'
          } else if (tier.bonusAwardedPercentage <= 0) {
            tierError.bonusAwardedPercentage = 'Bonus awarded percentage must be greater than 0'
          } else if (tier.bonusAwardedPercentage > 100) {
            tierError.bonusAwardedPercentage =
              'Bonus awarded percentage must be less than or equal to 100'
          }

          return tierError
        })

        if (tierErrors.some((tierError) => Object.keys(tierError).length > 0)) {
          mbErrors.campaignMarginBonusTiers = tierErrors
        }
      }

      if (Object.keys(mbErrors).length > 0) {
        errors.marginBonusConfiguration = mbErrors
      }
    }

    return errors
  },
  isInitialValid: false,
  enableReinitialize: false,
})(AddCampaignDetailsFormUI)

const isLuckyDrawValid = (values: CampaignDetailsFormValues) => {
  return (
    isLuckyDrawExpirationIntervalIdValid(values) &&
    isLuckyDrawExpirationIntervalValueValid(values) &&
    isLuckyDrawMinValueAllowanceValid(values) &&
    isLuckyDrawMaxDailyUsableDrawChancesValid(values) &&
    isLuckyDrawMaxDailyGeneratedDrawChancesValid(values) &&
    isLuckyDrawMaxDrawChancesSingleTransactionValid(values) &&
    isLuckyDrawMaxWinChancesPerClientValid(values) &&
    isLuckyDrawMaxWeeklyMaximumRewardsAllowedValid(values)
  )
}

const isLuckyDrawExpirationIntervalIdValid = (values: CampaignDetailsFormValues) => {
  return (
    values?.luckyDrawConfiguration?.expirationIntervalId &&
    values.luckyDrawConfiguration.expirationIntervalId >= 0
  )
}

const isLuckyDrawExpirationIntervalValueValid = (values: CampaignDetailsFormValues) => {
  return (
    values?.luckyDrawConfiguration?.expirationIntervalValue &&
    values.luckyDrawConfiguration.expirationIntervalValue > 0
  )
}

const isLuckyDrawMinValueAllowanceValid = (values: CampaignDetailsFormValues) => {
  return (
    values?.luckyDrawConfiguration?.minValueAllowance &&
    values.luckyDrawConfiguration.minValueAllowance > 0
  )
}

const isLuckyDrawMaxDailyUsableDrawChancesValid = (values: CampaignDetailsFormValues) => {
  return (
    values?.luckyDrawConfiguration?.maxDailyUsableDrawChances &&
    values.luckyDrawConfiguration.maxDailyUsableDrawChances > 0
  )
}

const isLuckyDrawMaxDailyGeneratedDrawChancesValid = (values: CampaignDetailsFormValues) => {
  return (
    values?.luckyDrawConfiguration?.maxDailyGeneratedDrawChances &&
    values.luckyDrawConfiguration.maxDailyGeneratedDrawChances > 0
  )
}

const isLuckyDrawMaxDrawChancesSingleTransactionValid = (values: CampaignDetailsFormValues) => {
  return (
    values?.luckyDrawConfiguration?.maxDrawChancesSingleTransaction &&
    values.luckyDrawConfiguration.maxDrawChancesSingleTransaction > 0
  )
}

const isLuckyDrawMaxWinChancesPerClientValid = (values: CampaignDetailsFormValues) => {
  return (
    values?.luckyDrawConfiguration?.maxWinChancesPerClient &&
    values.luckyDrawConfiguration.maxWinChancesPerClient > 0
  )
}

const isLuckyDrawMaxWeeklyMaximumRewardsAllowedValid = (values: CampaignDetailsFormValues) => {
  return (
    values?.luckyDrawConfiguration?.maxWeeklyMaximumRewardsAllowed &&
    values.luckyDrawConfiguration.maxWeeklyMaximumRewardsAllowed > 0
  )
}

const isMaxPayoutValid = (values: CampaignDetailsFormValues) => {
  return (
    !isEmpty(values?.maxPayout) || (typeof values?.maxPayout === 'number' && values?.maxPayout <= 0)
  )
}

const isLuckyDraw = (
  props: FormikProps<CampaignDetailsFormValues> | { values: CampaignDetailsFormValues }
) => {
  const { values } = props
  return (
    isNumeric(values.typeId) && [MarketingCampaignType.LuckyDraw].includes(toTypeId(values.typeId))
  )
}

const isCampaignOptions = (
  props: FormikProps<CampaignDetailsFormValues> | { values: CampaignDetailsFormValues }
) => {
  const { values } = props
  return (
    isNumeric(values.typeId) &&
    [MarketingCampaignType.ClientRebate, MarketingCampaignType.ClientContest].includes(
      toTypeId(values.typeId)
    )
  )
}

const isCampaignRebateOption = (
  props: FormikProps<CampaignDetailsFormValues> | { values: CampaignDetailsFormValues }
) => {
  const { values } = props
  return (
    isNumeric(values.typeId) &&
    [MarketingCampaignType.ClientRebate].includes(toTypeId(values.typeId))
  )
}

export const isMarginBonusType = (typeId: string | number | undefined) => {
  return typeId !== undefined && toTypeId(typeId) === MarketingCampaignType.MarginBonus
}

const toTypeId = (typeId: string | number) => {
  return isString(typeId) ? Number.parseInt(typeId, 10) : typeId
}
